import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import _isEqual from 'lodash/isEqual';
import _xorBy from 'lodash/xorBy';
import {
	Avatar,
	RunnitNodeRun,
	RunnitNodeField,
	RunnitNodeRunResult,
	RunnitUpload,
	LoadedAsset,
} from '@run-diffusion/shared';
import { NODE_RUN_RESULT_SELECTION_MODE, NodeRunResultSelectionMode } from '@/views/Runnits/constants';
import { emptySelectionState, RunnitSelectionStateType, SelectionState } from '@/store';
import type { ImgsOriginalsMap } from '@/utils/imgsVisualPromptTarget';

/**
 * Because there can be multiple inputes fighting for the the attention of the selection state but only one can
 *  truly be active at a time we have the following usage
 *
 * the top-level selectionStateHistory is a map of current or previously used selection states.
 *  The key for this map is the id of the source requesting the selection (inputId, top-menu-global download/delete, etc)
 *  The value of the map is a SelectionState object.
 *  In the SelectionState object there is a sortOrder property. This property is used to determine which state currently has the focus.
 *      since 0 is a falsey value, we'll use a sortOrder of 0 to mean that it is not active.
 *      The higher the sortOrder number the later is was added as selected, so the higher precedence it has to be the focused selection state
 * I went with a map instead of an array, because the side drawer input image selection state would open or close while doing the top menu
 *      selecting and trying to simply pop off an array was proving to be too difficult.
 * The hope is that since each selection state lives in the map it can work independently of the other selections and not get cleared
 *      or overwritten by a different input.
 */

export const RunnitsImageSelectMixin = Vue.extend({
	computed: {
		...mapState([
			'runnitState',
			'selectionStateHistory',
		]),
		...mapGetters([
			'orderedSelectionStates',
			'currentSelectionState',
			'getSelectionStateByTrigger',
		]),
	},
	methods: {
		...mapActions([
			'setSelectionStateHistory',
		]),
		getSelectionStateHistoryClone (): Record<string, SelectionState> {
			return { ...this.selectionStateHistory };
		},
		/**
		 * Gets the next number in the sort order because the largest number is the priority.
		 * If the triggeringId is already linked to the largest sortOrder then return that same number
		 * @param triggeringId
		 */
		getNextPriorityNumber (triggeringId): number {
			if (!this.currentSelectionState) return 1;

			if (this.currentSelectionState.triggeringId === triggeringId) return this.currentSelectionState.sortOrder;

			return this.currentSelectionState.sortOrder + 1;
		},
		/**
		 * Adds a new selection state to the selectionStateHistory, or updates the sortOrder to give it precedence again
		 * @param nodeRunResultSelectionMode
		 * @param triggeringId
		 * @param fieldAwaitingImageGallerySelection
		 */
		enterSelectionMode (nodeRunResultSelectionMode: NodeRunResultSelectionMode, triggeringId: string, fieldAwaitingImageGallerySelection: RunnitNodeField, canSelectTypes: RunnitSelectionStateType[]) {
			const nextPriorityNumber: number = this.getNextPriorityNumber(triggeringId);

			const selectionStateHistory = this.getSelectionStateHistoryClone();
			const existingSelectionState: SelectionState = selectionStateHistory[triggeringId] ? { ...selectionStateHistory[triggeringId] } : {};

			selectionStateHistory[triggeringId] = {
				...emptySelectionState,
				...existingSelectionState,
				nodeRunResultSelectionMode,
				triggeringId,
				canSelectTypes,
				fieldAwaitingImageGallerySelection,
				sortOrder: nextPriorityNumber,
				...(nodeRunResultSelectionMode === NODE_RUN_RESULT_SELECTION_MODE.SINGLE && {
					selectedNodeRunsMap: {},
					selectedNodeRunResults: [],
					selectedLoadedAssets: [],
					selectedUploads: [],
					selectedAvatars: [],
				}),
			};
			this.setSelectionStateHistory(selectionStateHistory);
		},
		/**
		 * sets the current active selection state (largest sortOrder number) to have a sortOrder of 0
		 * @param triggeringId
		 */
		exitSelectionMode (triggeringId: string) {
			const selectionStateHistory: Record<string, SelectionState> = this.getSelectionStateHistoryClone();
			const existingSelectionState = selectionStateHistory[triggeringId] ? { ...selectionStateHistory[triggeringId] } : {};
			selectionStateHistory[triggeringId] = {
				...emptySelectionState,
				...existingSelectionState,
				sortOrder: 0,
			};
			this.setSelectionStateHistory(selectionStateHistory);
		},
		updateSelectionState (selectionState: SelectionState) {
			const selectionStateHistory: Record<string, SelectionState> = this.getSelectionStateHistoryClone();
			selectionStateHistory[selectionState.triggeringId] = {
				...emptySelectionState,
				...selectionState,
			};
			this.setSelectionStateHistory(selectionStateHistory);
		},
		getSelectionStatePayloadFingerprint (selectionState: SelectionState | null | undefined): string {
			if (!selectionState) return '';

			const selectedNodeRunResults = (selectionState.selectedNodeRunResults || [])
				.map(({ uuid }) => uuid)
				.filter(Boolean)
				.join(',');
			const selectedUploads = (selectionState.selectedUploads || [])
				.map(({ id }) => id)
				.filter(Boolean)
				.join(',');

			return [
				selectionState.fieldAwaitingImageGallerySelection?.uuid || '',
				selectedNodeRunResults,
				selectedUploads,
			].join('|');
		},
		isSelectionStateForField (selectionState: SelectionState | null | undefined, fieldUuid: string): boolean {
			return !!(
				selectionState &&
				selectionState.fieldAwaitingImageGallerySelection &&
				selectionState.fieldAwaitingImageGallerySelection.uuid === fieldUuid
			);
		},
		hasSelectionStatePayloadChanged (
			newSelectionState: SelectionState | null | undefined,
			oldSelectionState: SelectionState | null | undefined,
		): boolean {
			return !_isEqual(
				this.getSelectionStatePayloadFingerprint(newSelectionState),
				this.getSelectionStatePayloadFingerprint(oldSelectionState),
			);
		},
		shouldSyncFieldSelectionState (
			newSelectionState: SelectionState | null | undefined,
			oldSelectionState: SelectionState | null | undefined,
			fieldUuid: string,
		): boolean {
			return this.isSelectionStateForField(newSelectionState, fieldUuid) &&
				this.hasSelectionStatePayloadChanged(newSelectionState, oldSelectionState);
		},
		commitSingleSelectionStateValue (
			newSelectionState: SelectionState | null | undefined,
			oldSelectionState: SelectionState | null | undefined,
			fieldUuid: string,
			currentValueNodeRunResultUuid: string | null,
			currentValueUploadId: string | null,
			onClear: () => void,
			onSelect: (value: { nodeRunResult?: RunnitNodeRunResult, upload?: RunnitUpload }) => void,
		): boolean {
			if (!this.shouldSyncFieldSelectionState(newSelectionState, oldSelectionState, fieldUuid)) return false;

			const firstSelectedNodeRunResult: RunnitNodeRunResult = this._get(newSelectionState, 'selectedNodeRunResults[0]') || null;
			const firstSelectedUpload: RunnitUpload = this._get(newSelectionState, 'selectedUploads[0]') || null;

			if (!firstSelectedNodeRunResult && !firstSelectedUpload && (currentValueNodeRunResultUuid || currentValueUploadId)) {
				onClear();
				return true;
			}

			if (firstSelectedNodeRunResult && firstSelectedNodeRunResult.uuid !== currentValueNodeRunResultUuid) {
				onSelect({ nodeRunResult: firstSelectedNodeRunResult });
				return true;
			}

			if (firstSelectedUpload && firstSelectedUpload.id !== currentValueUploadId) {
				onSelect({ upload: firstSelectedUpload });
				return true;
			}

			return false;
		},
		getCommittedMultiSelectionState (
			newSelectionState: SelectionState | null | undefined,
			oldSelectionState: SelectionState | null | undefined,
			fieldUuid: string,
		): { selectedNodeRunResults: RunnitNodeRunResult[], selectedUploads: RunnitUpload[] } | null {
			if (!this.shouldSyncFieldSelectionState(newSelectionState, oldSelectionState, fieldUuid)) return null;

			return {
				selectedNodeRunResults: newSelectionState.selectedNodeRunResults || [],
				selectedUploads: newSelectionState.selectedUploads || [],
			};
		},
		getPromptSelectionStateAsset (
			newSelectionState: SelectionState | null | undefined,
			oldSelectionState: SelectionState | null | undefined,
		): { nodeRunResult?: RunnitNodeRunResult, upload?: RunnitUpload } | null {
			if (!newSelectionState?.fieldAwaitingImageGallerySelection) return null;
			if (!this.hasSelectionStatePayloadChanged(newSelectionState, oldSelectionState)) return null;

			const firstSelectedNodeRunResult: RunnitNodeRunResult = this._get(newSelectionState, 'selectedNodeRunResults[0]') || null;
			if (firstSelectedNodeRunResult) {
				return { nodeRunResult: firstSelectedNodeRunResult };
			}

			const firstSelectedUpload: RunnitUpload = this._get(newSelectionState, 'selectedUploads[0]') || null;
			if (firstSelectedUpload) {
				return { upload: firstSelectedUpload };
			}

			return null;
		},
		buildSelectedNodeRunsMapFromResults (nodeRunResults: RunnitNodeRunResult[]): Record<string, { nodeRun: any, results: RunnitNodeRunResult[] }> {
			const selectedNodeRunsMap: Record<string, { nodeRun: any, results: RunnitNodeRunResult[] }> = {};
			(nodeRunResults || []).forEach((nodeRunResult: RunnitNodeRunResult) => {
				const nodeRunId = nodeRunResult.nodeRunId;
				if (!selectedNodeRunsMap[nodeRunId]) {
					selectedNodeRunsMap[nodeRunId] = {
						nodeRun: { id: nodeRunId },
						results: [],
					};
				}
				selectedNodeRunsMap[nodeRunId].results.push(nodeRunResult);
			});
			return selectedNodeRunsMap;
		},
		syncImgsSelectionState (
			{
				triggeringId,
				fieldAwaitingImageGallerySelection,
				uploads,
				nodeRunResults,
				canSelectTypes = ['IMG'],
				nodeRunResultSelectionMode = NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE,
				keepActive = false,
				enterMode = false,
				originalImages = null,
			}: {
				triggeringId: string
				fieldAwaitingImageGallerySelection: RunnitNodeField
				uploads: RunnitUpload[]
				nodeRunResults: RunnitNodeRunResult[]
				canSelectTypes?: RunnitSelectionStateType[]
				nodeRunResultSelectionMode?: NodeRunResultSelectionMode
				keepActive?: boolean
				enterMode?: boolean
				originalImages?: ImgsOriginalsMap | null
			},
		) {
			const existingSelectionState: Partial<SelectionState> = this.getSelectionStateByTrigger(triggeringId) || {};
			const sortOrder = keepActive
				? this.getNextPriorityNumber(triggeringId)
				: (existingSelectionState.sortOrder || 0);
			this.updateSelectionState({
				...existingSelectionState,
				triggeringId,
				sortOrder,
				nodeRunResultSelectionMode,
				canSelectTypes,
				fieldAwaitingImageGallerySelection,
				selectedNodeRunsMap: this.buildSelectedNodeRunsMapFromResults(nodeRunResults),
				selectedNodeRunResults: nodeRunResults,
				selectedLoadedAssets: [],
				selectedUploads: uploads,
				selectedAvatars: [],
				// null means "unknown here, keep what's there"; {} is a real "no originals".
				originalImages: originalImages !== null ? originalImages : (existingSelectionState.originalImages || {}),
			});
			if (enterMode) {
				this.enterSelectionMode(nodeRunResultSelectionMode, triggeringId, fieldAwaitingImageGallerySelection, canSelectTypes);
			}
		},
		clearSelectionsFromState (triggeringId: string) {
			this.updateSelectionState({
				...this.getSelectionStateByTrigger(triggeringId),
				selectedNodeRunsMap: {},
				selectedNodeRunResults: [],
				selectedLoadedAssets: [],
				selectedUploads: [],
				selectedAvatars: [],
			});
		},
		onNodeRunResultSingleSelection (nodeRun: RunnitNodeRun, nodeRunResult: RunnitNodeRunResult) {
			if (!this.currentSelectionState.canSelectTypes.includes(nodeRunResult.type)) return;

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				nodeRunResultSelectionMode: null,
				selectedNodeRunsMap: {
					...this.currentSelectionState.selectedNodeRunsMap,
					[nodeRun.id]: {
						nodeRun,
						results: [nodeRunResult],
					},
				},
				selectedNodeRunResults: [nodeRunResult],
				selectedLoadedAssets: [],
				selectedUploads: [],
				selectedAvatars: [],
				sortOrder: 0,
			};
			this.updateSelectionState(selectionState);
		},
		onNodeRunResultMultiSelection (nodeRun: RunnitNodeRun, nodeRunResult: RunnitNodeRunResult) {
			if (!this.currentSelectionState.canSelectTypes.includes(nodeRunResult.type)) return;

			const selectedNodeRunResultsForNodeRun: RunnitNodeRunResult[] = _xorBy(
				[...(this.currentSelectionState.selectedNodeRunsMap?.[nodeRun.id]?.results || [])],
				[nodeRunResult],
				(n: RunnitNodeRunResult) => n.uuid,
			);
			const selectedNodeRunsMap = { ...this.currentSelectionState.selectedNodeRunsMap };
			if (selectedNodeRunResultsForNodeRun.length) {
				selectedNodeRunsMap[nodeRun.id] = {
					nodeRun,
					results: selectedNodeRunResultsForNodeRun,
				};
			} else {
				delete selectedNodeRunsMap[nodeRun.id];
			}

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				selectedNodeRunsMap,
				selectedNodeRunResults: _xorBy(
					[...(this.currentSelectionState.selectedNodeRunResults || [])],
					[nodeRunResult],
					(n: RunnitNodeRunResult) => n.uuid,
				),
				selectedLoadedAssets: [...(this.currentSelectionState.selectedLoadedAssets || [])],
				selectedUploads: [...(this.currentSelectionState.selectedUploads || [])],
				selectedAvatars: [...(this.currentSelectionState.selectedAvatars || [])],
			};
			this.updateSelectionState(selectionState);
		},
		onLoadedAssetSingleSelection (loadedAsset: LoadedAsset) {
			if (!this.currentSelectionState.canSelectTypes.includes(loadedAsset.nodeRunResult.type)) return;

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				nodeRunResultSelectionMode: null,
				selectedNodeRunsMap: {},
				selectedNodeRunResults: [],
				selectedLoadedAssets: [loadedAsset],
				selectedUploads: [],
				selectedAvatars: [],
				sortOrder: 0,
			};
			this.updateSelectionState(selectionState);
		},
		onLoadedAssetMultiSelection (loadedAsset: LoadedAsset) {
			if (!this.currentSelectionState.canSelectTypes.includes(loadedAsset.nodeRunResult.type)) return;

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				selectedNodeRunsMap: { ...this.currentSelectionState.selectedNodeRunsMap },
				selectedNodeRunResults: [...(this.currentSelectionState.selectedNodeRunResults || [])],
				selectedLoadedAssets: _xorBy(
					[...(this.currentSelectionState.selectedLoadedAssets || [])],
					[loadedAsset],
					(la: LoadedAsset) => la.src === loadedAsset.src,
				),
				selectedUploads: [...(this.currentSelectionState.selectedUploads || [])],
				selectedAvatars: [...(this.currentSelectionState.selectedAvatars || [])],
			};
			this.updateSelectionState(selectionState);
		},
		onUploadSingleSelection (upload: RunnitUpload) {
			if (!this.currentSelectionState.canSelectTypes.includes(upload.type)) return;

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				nodeRunResultSelectionMode: null,
				selectedNodeRunsMap: {},
				selectedNodeRunResults: [],
				selectedLoadedAssets: [],
				selectedUploads: [upload],
				selectedAvatars: [],
				sortOrder: 0,
			};
			this.updateSelectionState(selectionState);
		},
		onUploadMultiSelection (upload: RunnitUpload) {
			if (!this.currentSelectionState.canSelectTypes.includes(upload.type)) return;

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				selectedNodeRunsMap: { ...this.currentSelectionState.selectedNodeRunsMap },
				selectedNodeRunResults: [...(this.currentSelectionState.selectedNodeRunResults || [])],
				selectedLoadedAssets: [...(this.currentSelectionState.selectedLoadedAssets || [])],
				selectedUploads: _xorBy(
					[...(this.currentSelectionState.selectedUploads || [])],
					[upload],
					(u: RunnitUpload) => u.id,
				),
				selectedAvatars: [...(this.currentSelectionState.selectedAvatars || [])],
			};
			this.updateSelectionState(selectionState);
		},
		onAvatarSingleSelection (avatar: Avatar) {
			if (!this.currentSelectionState.canSelectTypes.includes(avatar.type)) return;

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				nodeRunResultSelectionMode: null,
				selectedNodeRunsMap: {},
				selectedNodeRunResults: [],
				selectedLoadedAssets: [],
				selectedUploads: [],
				selectedAvatars: [avatar],
				sortOrder: 0,
			};
			this.updateSelectionState(selectionState);
		},
		onAvatarMultiSelection (avatar: Avatar) {
			if (!this.currentSelectionState.canSelectTypes.includes(avatar.type)) return;

			const selectionState: SelectionState = {
				...this.currentSelectionState,
				selectedNodeRunsMap: { ...this.currentSelectionState.selectedNodeRunsMap },
				selectedNodeRunResults: [...(this.currentSelectionState.selectedNodeRunResults || [])],
				selectedLoadedAssets: [...(this.currentSelectionState.selectedLoadedAssets || [])],
				selectedUploads: [...(this.currentSelectionState.selectedUploads || [])],
				selectedAvatars: _xorBy(
					[...(this.currentSelectionState.selectedAvatars || [])],
					[avatar],
					(a: Avatar) => a.id,
				),
			};
			this.updateSelectionState(selectionState);
		},
		clearAllSelectionStates () {
			const activeSelectionStates = this.orderedSelectionStates;

			// Exit selection mode for all active selection states
			activeSelectionStates.forEach(state => {
				if (state.triggeringId) {
					this.exitSelectionMode(state.triggeringId);
				}
			});
		},
	},
});
